/**
 * DynamicButton - Ein vielseitiger Button mit umfangreichen Konfigurationsmöglichkeiten
 *
 * Unterstützte Button-Typen:
 * - 'icon': Nur Icon
 * - 'text': Nur Text
 * - 'icon-text': Icon und Text nebeneinander
 * - 'icon-full': Icon füllt den gesamten Button-Bereich
 *
 * Features:
 * - Toggle-Funktionalität mit verschiedenen Icons
 * - Spinner-Anzeige
 * - Flexible Event-Handler (mit oder ohne Event-Parameter)
 * - Umfangreiche Styling-Optionen
 * - Responsive Icon-Größen
 */
import React, { useState } from 'react';
import { ReactNode } from 'react';
import { IonButton } from '@ionic/react';
import Icon from '../icons/Icon';
import { BUTTON_PADDING } from '../../constants/ui';
import { min } from 'lodash';
import AnimatedFreakSpinner from '../shared/AnimatedFreakSpinner';
import './DynamicButton.css';

/**
 * Größendefinitionen für verschiedene Button-Größen in Pixeln
 * Diese Werte werden für die Icon-Container und Button-Dimensionen verwendet
 */
const BUTTON_SIZES = {
  xsmall: 24,
  small: 35,
  default: 40,
  large: 48,
  xlarge: 56,
};

// Type-Definitionen für bessere TypeScript-Unterstützung
type CustomSize = 'xsmall' | 'small' | 'default' | 'large' | 'xlarge';
type ExpandOption = 'block' | 'full'; // Ionic Button expand Optionen
type AlignOption = 'start' | 'center' | 'end'; // Content-Ausrichtung innerhalb des Buttons
type ButtonType = 'icon' | 'text' | 'icon-text' | 'icon-full'; // Verschiedene Button-Darstellungstypen

/**
 * Props Interface für den DynamicButton
 * Unterstützt verschiedene Button-Typen und Konfigurationen
 */
interface DynamicButtonProps {
  // Icon-bezogene Props
  iconName?: string; // Name des primären Icons
  iconNameToggle?: string; // Icon für Toggle-Zustand (veraltet, verwende toggleIconOff)
  iconCode?: string; // Alternativer Icon-Code

  // Text-bezogene Props
  text?: string | ReactNode; // Button-Text oder React-Element

  // Event-Handler - unterstützt sowohl Event-basierte als auch Event-lose Callbacks
  onClick: ((event: React.MouseEvent) => void) | (() => void);

  // Styling Props
  color?: string; // Ionic Farbschema
  fill?: 'clear' | 'outline' | 'solid' | 'default'; // Ionic Button fill
  size?: CustomSize; // Button-Größe
  style?: React.CSSProperties; // Zusätzliche CSS-Styles
  className?: string; // CSS-Klassen
  borderRadius?: string; // Border-Radius für den Button
  backgroundColor?: string; // Hintergrundfarbe
  textColor?: string; // Textfarbe
  iconColor?: string; // Spezifische Icon-Farbe (überschreibt automatische Berechnung)

  // Button-Verhalten
  buttonType?: ButtonType; // Typ des Buttons (Icon, Text, Icon+Text, Icon-Full)
  disabled?: boolean; // Deaktiviert-Status
  id?: string; // HTML-ID
  expand?: ExpandOption; // Ionic expand Option
  align?: AlignOption; // Content-Ausrichtung

  // Toggle-Funktionalität
  isToggleable?: boolean; // Aktiviert Toggle-Verhalten
  toggled?: boolean; // Aktueller Toggle-Zustand (kontrolliert von außen)
  onToggleChange?: (newState: boolean) => void; // Callback bei Toggle-Änderung
  toggleIconOn?: string; // Icon wenn toggled=true
  toggleIconOff?: string; // Icon wenn toggled=false

  // Spinner-Funktionalität
  showSpinner?: boolean; // Zeigt Spinner anstatt Icon
}

/**
 * Mapping von Custom-Größen zu Ionic Button-Größen
 * Da Ionic nur 3 Größen unterstützt, werden mehrere Custom-Größen auf diese gemappt
 */
const sizeMap: Record<CustomSize, 'small' | 'default' | 'large'> = {
  xsmall: 'small',
  small: 'small',
  default: 'default',
  large: 'large',
  xlarge: 'large',
};

const DynamicButton: React.FC<DynamicButtonProps> = ({
  iconName,
  iconNameToggle,
  iconCode,
  text,
  onClick,
  color = 'primary',
  fill = 'solid',
  size = 'default',
  style = {},
  className = '',
  borderRadius = '8px',
  buttonType = 'icon',
  disabled = false,
  expand = undefined,
  align = 'start',
  backgroundColor,
  textColor,
  iconColor, // Neue iconColor Property
  isToggleable,
  toggled,
  onToggleChange,
  toggleIconOn,
  toggleIconOff,
  showSpinner = false,
}) => {
  // Hole das Padding basierend auf der Button-Größe
  const padding = BUTTON_PADDING[size];

  // Toggle-Zustandsverwaltung
  // Interner Zustand für unkontrollierte Toggle-Buttons
  const [internalToggle, setInternalToggle] = useState(false);
  // Verwende externen Zustand falls vorhanden, sonst internen Zustand
  const isToggled = toggled ?? internalToggle;

  // Icon-Bestimmungslogik
  // Bestimme das Basis-Icon (iconCode hat Vorrang vor iconName)
  const effectiveIconName = iconCode || iconName;

  // Berechne das anzuzeigende Icon basierend auf Toggle-Zustand
  const computedIconName = isToggleable
    ? isToggled
      ? iconName || toggleIconOn || effectiveIconName // Toggle EIN: Verwende spezifisches Icon oder Fallback
      : iconNameToggle || toggleIconOff || effectiveIconName // Toggle AUS: Verwende alternatives Icon oder Fallback
    : effectiveIconName; // Kein Toggle: Verwende Basis-Icon

  // Icon-Farbe bestimmen - Priorität: explizite iconColor > textColor > Fallback basierend auf Button-Farbe
  const computedIconColor = iconColor || textColor || (color === 'secondary' ? 'white' : '#82368c');

  // Mapping für Content-Ausrichtung (CSS Flexbox justify-content Werte)
  const alignmentMap: Record<AlignOption, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
  };

  /**
   * Zentrale Click-Handler Funktion
   * Verwaltet sowohl Toggle-Logik als auch onClick-Weiterleitung
   * Unterstützt sowohl Event-basierte als auch Event-lose onClick-Handler für Rückwärtskompatibilität
   */
  const handleClick = (event: React.MouseEvent) => {
    // Toggle-Logik ausführen falls aktiviert
    if (isToggleable) {
      const newState = !isToggled;
      // Externe Toggle-Änderung mitteilen
      if (onToggleChange) onToggleChange(newState);
      // Internen Zustand nur ändern wenn nicht extern kontrolliert
      if (toggled === undefined) setInternalToggle(newState);
    }

    // onClick-Handler aufrufen - prüfe ob Event-Parameter erwartet wird
    // Dies ermöglicht sowohl () => void als auch (event: MouseEvent) => void Handler
    if (onClick.length > 0) {
      // Handler erwartet Event-Parameter
      (onClick as (event: React.MouseEvent) => void)(event);
    } else {
      // Handler erwartet keine Parameter
      (onClick as () => void)();
    }
  };

  /**
   * Generiert den Icon-Content basierend auf dem Button-Typ
   * Behandelt sowohl normale Icons als auch Spinner-Anzeige
   * Unterscheidet zwischen 'icon-full' (füllt gesamten Button) und anderen Typen
   */
  const getIconContent = () => {
    // Kein Content wenn weder Icon noch Spinner vorhanden
    if (!computedIconName && !showSpinner) return null;

    if (buttonType === 'icon-full') {
      // Icon-Full: Icon wird größer dargestellt und füllt den gesamten Button-Bereich
      // Berechne größere Icon-Größe für bessere Sichtbarkeit
      const iconSize: CustomSize =
        size === 'xsmall'
          ? 'small'
          : size === 'small'
          ? 'default'
          : size === 'default'
          ? 'large'
          : 'xlarge';

      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: `${BUTTON_SIZES[size]}px`,
              height: `${BUTTON_SIZES[size]}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {showSpinner ? (
              <AnimatedFreakSpinner
                size={42} // Größerer Spinner für icon-full Buttons
                color={computedIconColor}
                duration={1000}
              />
            ) : computedIconName ? (
              <Icon
                name={computedIconName}
                size={iconSize} // Verwende die berechnete größere Icon-Größe
                color={computedIconColor}
              />
            ) : null}
          </div>
        </div>
      );
    }

    // Normale Icon-Darstellung mit einheitlichem Container
    return (
      <div
        style={{
          width: `${BUTTON_SIZES[size]}px`,
          height: `${BUTTON_SIZES[size]}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {showSpinner ? (
          <AnimatedFreakSpinner
            size={33} // Standard-Spinner-Größe für normale Buttons
            color={computedIconColor}
            duration={1000}
          />
        ) : computedIconName ? (
          <Icon name={computedIconName} size={size} color={computedIconColor} />
        ) : null}
      </div>
    );
  };

  /**
   * Generiert Button-spezifische Styles basierend auf dem Button-Typ
   * Unterscheidet zwischen icon-full (ohne Padding) und anderen Typen (mit Padding)
   */
  const getButtonStyles = () => {
    const baseStyles = {
      '--border-radius': borderRadius,
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      backgroundColor: backgroundColor || undefined,
      ...style, // Übernehme zusätzliche Styles vom Parent
    };

    // Icon-full Buttons benötigen kein Padding da das Icon den gesamten Bereich füllt
    if (buttonType === 'icon-full') {
      return {
        ...baseStyles,
        '--padding-start': '0',
        '--padding-end': '0',
      };
    }

    // Alle anderen Button-Typen verwenden das Standard-Padding
    return {
      ...baseStyles,
      '--padding-start': padding,
      '--padding-end': padding,
    };
  };

  /**
   * Generiert Container-Styles für den Button-Content
   * Passt Layout-Eigenschaften basierend auf dem Button-Typ an
   */
  const getContainerStyles = () => {
    const baseStyles = {
      display: 'flex',
      // Icon-Text Buttons: Links ausgerichtet, andere: Konfigurierbare Ausrichtung
      justifyContent:
        buttonType === 'icon-text' ? 'flex-start' : alignmentMap[align],
      // Icon-Text Buttons: Vertikale Zentrierung für bessere Optik
      alignItems: buttonType === 'icon-text' ? 'center' : 'flex-start',
      gap: '8px', // Abstand zwischen Icon und Text
      width: '100%',
      // Icon-Text Buttons: Feste Mindesthöhe basierend auf Icon-Größe
      minHeight:
        buttonType === 'icon-text' ? `${BUTTON_SIZES[size]}px` : undefined,
      padding: buttonType === 'icon-text' ? '4px 0' : undefined,
      textAlign: 'center' as const,
    };

    // Icon-full Buttons: Container füllt gesamte Höhe
    return buttonType === 'icon-full'
      ? { ...baseStyles, height: '100%' }
      : { ...baseStyles, height: 'auto' };
  };

  /**
   * Helper-Funktion für besseres Text-Wrapping
   * Fügt Zero-Width Space nach Trennzeichen ein um Umbrüche zu ermöglichen
   */
  const wrapText = (text: string) => {
    return text.replace(/[-_\s]/g, '$&\u200B'); // \u200B = Zero-Width Space
  };

  /**
   * Generiert den Text-Content mit angepasstem Styling
   * Behandelt sowohl String- als auch ReactNode-Text
   */
  const getTextContent = (text: string | ReactNode) => {
    if (typeof text === 'string') {
      return (
        <span
          style={{
            color: textColor,
            wordBreak: 'break-word', // Ermöglicht Wortumbrüche
            whiteSpace: 'pre-wrap', // Respektiert Whitespace und Zeilenumbrüche
            hyphens: 'auto', // Automatische Silbentrennung
            // Maximale Breite: Bei icon-text berücksichtige Icon-Breite und Gap
            maxWidth:
              buttonType === 'icon-text'
                ? `calc(100% - ${BUTTON_SIZES[size]}px - 8px)`
                : '100%',
            lineHeight: '1.2', // Optimierte Zeilenhöhe für Lesbarkeit
            flexGrow: 1, // Text nimmt verfügbaren Platz ein
            textAlign: 'left', // Linksbündig für bessere Lesbarkeit
            marginLeft: buttonType === 'icon-text' ? '0' : undefined,
          }}
        >
          {wrapText(text)}
        </span>
      );
    }
    // ReactNode direkt zurückgeben ohne Wrapper
    return text;
  };

  // Render des finalen Button-Elements
  return (
    <IonButton
      disabled={disabled}
      className={`dynamic-button ${buttonType} ${
        buttonType === 'icon-full' ? `icon-full-button ${size}` : ''
      } ${className}`}
      color={color}
      fill={fill}
      size={sizeMap[size]}
      expand={expand}
      onClick={handleClick}
      style={getButtonStyles()}
    >
      {/* Content-Container mit flexiblem Layout */}
      <div
        style={{
          ...getContainerStyles(),
          // Icon-Text: Kein Wrapping (Icon und Text bleiben in einer Zeile)
          flexWrap: buttonType === 'icon-text' ? 'nowrap' : 'wrap',
        }}
      >
        {/* Conditional Rendering basierend auf Button-Typ */}
        {buttonType === 'icon-full' ? (
          // Icon-Full: Nur Icon-Content
          getIconContent()
        ) : (
          <>
            {/* Icon-Only Button */}
            {buttonType === 'icon' && (computedIconName || showSpinner) ? (
              getIconContent()
            ) : /* Text-Only Button */
            buttonType === 'text' && text ? (
              getTextContent(text)
            ) : /* Icon-Text Button: Beide Elemente erforderlich */
            buttonType === 'icon-text' &&
              (computedIconName || showSpinner) &&
              text ? (
              <>
                {getIconContent()}
                {getTextContent(text)}
              </>
            ) : null}
          </>
        )}
      </div>
    </IonButton>
  );
};

export default DynamicButton;
